import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";

import { NextUp } from "@/components/Callouts";

export default function EarTraining8() {
  const { lesson, course, accent } = findLesson("ear-training", "8");
  return (
    <LessonShell
      course={course}
      courseSlug="ear-training"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/ear-training/ET8-Counting-Beats-Animated.mp4"
        caption="Counting beats in real songs — the pulse keeps going"
        hint="≈ 20 sec · watch first"
      />
      <Chapter number="08" eyebrow="Ear Training · Chapter Eight" title="Counting beats in real songs">
        <Prose>
          <p>
            Lesson 3 introduced the three division systems (÷2, ÷3, ÷4). This lesson is
            the practical drill: listening to actual songs and counting them out. The
            workflow is the same every time — find the pulse, identify the time signature,
            then count out the sub-divisions.
          </p>
        </Prose>

        <MTQuote>
          The most important thing while you learn is to enjoy the song while you're
          learning. Don't look at it in a very studious way.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The four-step counting workflow" title="Pulse → time signature → divisions → grooves">
        <Steps>
          <Step><strong>Find the pulse.</strong> Tap your foot to the song. The rate at which your foot taps is the beat. That's it.</Step>
          <Step><strong>Identify the time signature.</strong> Count from 1 until the music tells you to start over. Most songs are 4/4 (count of four). Some are 3/4 (waltz). Rarer: 6/8 (compound duple) or 7/8 (asymmetric).</Step>
          <Step><strong>Identify the division.</strong> Between two foot-taps, do you feel two events (÷2 = eighths), three (÷3 = triplets), or four (÷4 = sixteenths)?</Step>
          <Step><strong>Identify the groove.</strong> Where do the kick and snare hits land? In rock the snare is on 2 and 4 (the backbeat). In waltz it's on 1 (the downbeat). In bossa nova the snare hits the off-beats. Knowing the groove tells you genre.</Step>
        </Steps>
      </Chapter>

      <Chapter eyebrow="The flowchart" title="One picture, every rhythm decision">
        <NoteImage
          src="/notes/ear-training/finals/03a-rhythmic-flowchart.png"
          caption="The same rhythmic flowchart from chapter 3 — but now we're using it as a real-time decision tree on actual recordings. Listen → Pulse → Time Signature → Time Feel → Rhythmic Devices."
        />

        <Prose>
          <p>
            Apply this flowchart to three songs of contrasting feel: a pop single (likely
            ÷2 straight), a jazz standard (likely ÷3 swing), and a hip-hop beat (likely ÷4
            with syncopated snare). Within minutes you'll have built the muscle to identify
            time-feel by ear in any song.
          </p>
        </Prose>
      </Chapter>

      <NextUp
        to="/lesson/ear-training-9"
        label="Chapter 9 · A Flowchart for Rhythm"
        why="We assemble everything we know about rhythm — pulse, time signature, time feel, devices — into a single flowchart you can run on any song in under a minute."
      />
    </LessonShell>
  );
}
